import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-rice">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><span className="w-9 h-9 bg-cinnabar text-rice flex items-center justify-center font-serif text-xl">中</span><span className="font-serif text-xl">CCLPT</span></Link>
          <h1 className="font-serif text-4xl">Create account</h1>
          <p className="text-clay mt-2 mb-8 text-sm">Join as a student or a teacher.</p>

          {error && <div className="mb-4 px-4 py-3 bg-cinnabar/10 border border-cinnabar/30 text-cinnabar text-sm" data-testid="register-error">{error}</div>}

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              {["student", "teacher"].map((r) => (
                <button type="button" key={r} onClick={() => setForm({ ...form, role: r })} data-testid={`role-${r}`}
                  className={`py-3 text-sm font-medium border capitalize transition-colors ${form.role === r ? "bg-ink text-rice border-ink" : "bg-white text-ink border-black/10 hover:border-cinnabar"}`}>
                  {r}
                </button>
              ))}
            </div>
            <div>
              <label className="label text-clay">Full name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name" className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
            </div>
            <div>
              <label className="label text-clay">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email" className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
            </div>
            <div>
              <label className="label text-clay">Password</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password" className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
            </div>
            <button disabled={busy} data-testid="register-submit"
              className="w-full py-3.5 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300 disabled:opacity-60">
              {busy ? "Creating…" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-black/10" /><span className="text-xs text-clay">OR</span><div className="flex-1 h-px bg-black/10" /></div>
          <button onClick={googleLogin} data-testid="register-google"
            className="w-full py-3.5 border border-black/15 bg-white font-medium hover:border-cinnabar transition-colors flex items-center justify-center gap-3">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
          </button>

          <p className="text-sm text-clay mt-8 text-center">
            Already registered? <Link to="/login" className="text-cinnabar font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block grain relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1569165755139-296fac054979?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-rice">
          <p className="font-serif text-4xl leading-tight">开始学习</p>
          <p className="text-rice/60 mt-2">Begin your journey to Mandarin mastery today.</p>
        </div>
      </div>
    </div>
  );
}
